/**
 * Ad management framework for handling advertisements across the site
 * This service manages ad configurations, tracking, and targeting
 */

// Types for ad configuration
export interface AdConfig {
  id: string;
  slot: string;
  size: string;
  imageUrl?: string;
  targetUrl?: string;
  displayConditions?: {
    device?: 'mobile' | 'desktop' | 'all';
    userSegments?: string[];
    countries?: string[];
    categories?: string[];
    isPremium?: boolean;
  }
}

// Ad impression tracking interface
export interface ImpressionData {
  adId: string;
  slot: string;
  timestamp: number;
  pageUrl: string;
  placement: string;
}

// Ad click tracking interface
export interface ClickData extends ImpressionData {
  targetUrl: string;
}

// Mock data for ads - in production this would come from your backend API
const adConfigs: AdConfig[] = [
  // Header ads (leaderboard)
  {
    id: 'premium-subscription',
    slot: 'header',
    size: 'leaderboard',
    imageUrl: 'https://placehold.co/728x90/e4e7eb/4b5563?text=Premium+Subscription',
    targetUrl: '/subscribe-premium',
  },
  {
    id: 'legal-conference',
    slot: 'header',
    size: 'leaderboard',
    imageUrl: 'https://placehold.co/728x90/e4e7eb/4b5563?text=Legal+Conference+2023',
    targetUrl: '/events/legal-conference',
  },
  
  // In-feed ads (rectangle)
  {
    id: 'law-school',
    slot: 'infeed',
    size: 'rectangle',
    imageUrl: 'https://placehold.co/336x280/e4e7eb/4b5563?text=Top+Law+Schools',
    targetUrl: '/law-schools',
    displayConditions: {
      categories: ['education', 'career']
    }
  },
  {
    id: 'legal-books',
    slot: 'infeed',
    size: 'rectangle',
    imageUrl: 'https://placehold.co/336x280/e4e7eb/4b5563?text=Legal+Books+Sale',
    targetUrl: '/resources/books',
  },
  
  // Sidebar ads (rectangle)
  {
    id: 'law-firms-jobs',
    slot: 'sidebar',
    size: 'rectangle',
    imageUrl: 'https://placehold.co/336x280/e4e7eb/4b5563?text=Law+Firm+Careers',
    targetUrl: '/law-firms/jobs',
  },
  {
    id: 'premium-tools',
    slot: 'sidebar',
    size: 'rectangle',
    imageUrl: 'https://placehold.co/336x280/e4e7eb/4b5563?text=Legal+Research+Tools',
    targetUrl: '/tools',
  },
  
  // Footer ads (leaderboard and mobile)
  {
    id: 'events-promo',
    slot: 'footer',
    size: 'leaderboard',
    imageUrl: 'https://placehold.co/728x90/e4e7eb/4b5563?text=Legal+Events',
    targetUrl: '/events',
  },
  {
    id: 'mobile-app',
    slot: 'footer',
    size: 'mobile',
    imageUrl: 'https://placehold.co/320x100/e4e7eb/4b5563?text=Mobile+App',
    targetUrl: '/mobile-app',
    displayConditions: {
      device: 'mobile'
    }
  }
];

/**
 * Checks if the user has premium subscription (ad-free experience)
 */
export const isPremiumUser = (): boolean => {
  // In a real implementation, this would check auth status and subscription
  return localStorage.getItem('premium_subscription') === 'true';
};

/**
 * Gets an ad configuration based on slot and size
 */
export const getAdConfig = (
  slot: string, 
  size: string,
  options: { category?: string, isPremiumContent?: boolean } = {}
): AdConfig | undefined => {
  // If user has premium (ad-free), don't show ads on premium content
  if (isPremiumUser() && options.isPremiumContent) {
    return undefined;
  }

  // Filter ads by slot and size
  const matchingAds = adConfigs.filter(ad => {
    // Basic matching by slot and size
    const basicMatch = ad.slot === slot && ad.size === size;
    
    // If no display conditions, just use basic match
    if (!ad.displayConditions) return basicMatch;
    
    // If category specific targeting is set, check category match
    if (ad.displayConditions.categories && options.category) {
      return basicMatch && ad.displayConditions.categories.some(cat => 
        options.category?.toLowerCase().includes(cat.toLowerCase())
      );
    }
    
    return basicMatch;
  });
  
  if (matchingAds.length === 0) {
    return undefined;
  }
  
  // In a real implementation, you'd have logic to select the best ad
  // For now, just return a random ad from the matching ones
  return matchingAds[Math.floor(Math.random() * matchingAds.length)];
};

/**
 * Tracks ad impression
 */
export const trackAdImpression = (adId: string, slot: string, placement: string): void => {
  const impressionData: ImpressionData = {
    adId,
    slot,
    placement,
    timestamp: Date.now(),
    pageUrl: window.location.href
  };
  
  // In production, send to analytics service
  console.log('Ad impression tracked:', impressionData);
  
  // You could store impressions locally and batch send them
  const impressions = JSON.parse(localStorage.getItem('ad_impressions') || '[]');
  impressions.push(impressionData);
  localStorage.setItem('ad_impressions', JSON.stringify(impressions.slice(-50))); // Keep last 50 impressions
};

/**
 * Tracks ad clicks
 */
export const trackAdClick = (adId: string, slot: string, targetUrl: string, placement: string): void => {
  const clickData: ClickData = {
    adId,
    slot,
    placement,
    timestamp: Date.now(),
    pageUrl: window.location.href,
    targetUrl
  };
  
  // In production, send to analytics service
  console.log('Ad click tracked:', clickData);
  
  // You could store clicks locally and batch send them
  const clicks = JSON.parse(localStorage.getItem('ad_clicks') || '[]');
  clicks.push(clickData);
  localStorage.setItem('ad_clicks', JSON.stringify(clicks.slice(-50))); // Keep last 50 clicks
};

/**
 * Checks if an ad should be shown based on user profile and page context
 */
export const shouldShowAd = (
  adConfig: AdConfig | undefined, 
  options: { 
    isArticlePage?: boolean, 
    category?: string, 
    isPremiumContent?: boolean
  } = {}
): boolean => {
  // No ad config found
  if (!adConfig) return false;
  
  // Premium users don't see ads
  if (isPremiumUser()) return false;
  
  // Premium content may have different ad rules
  if (options.isPremiumContent) {
    // Maybe show only certain types of ads on premium content
  }
  
  // Check device type if specified in display conditions
  if (adConfig.displayConditions?.device) {
    const isMobile = window.innerWidth < 768;
    if (
      (adConfig.displayConditions.device === 'mobile' && !isMobile) ||
      (adConfig.displayConditions.device === 'desktop' && isMobile)
    ) {
      return false;
    }
  }
  
  // Check category targeting
  if (adConfig.displayConditions?.categories && options.category) {
    const matchesCategory = adConfig.displayConditions.categories.some(cat => 
      options.category?.toLowerCase().includes(cat.toLowerCase())
    );
    
    if (!matchesCategory) return false;
  }
  
  return true;
};

// Function to sync ad impressions and clicks with backend
export const syncAdAnalytics = async (): Promise<void> => {
  try {
    // Get locally stored impressions and clicks
    const impressions = JSON.parse(localStorage.getItem('ad_impressions') || '[]');
    const clicks = JSON.parse(localStorage.getItem('ad_clicks') || '[]');
    
    if (impressions.length === 0 && clicks.length === 0) return;
    
    // In production, send to your backend
    console.log('Syncing ad analytics:', { impressions, clicks });
    
    // After successful sync, clear local storage
    localStorage.setItem('ad_impressions', '[]');
    localStorage.setItem('ad_clicks', '[]');
  } catch (error) {
    console.error('Failed to sync ad analytics:', error);
  }
};

export default {
  getAdConfig,
  trackAdImpression,
  trackAdClick,
  shouldShowAd,
  isPremiumUser,
  syncAdAnalytics
};